import { useCallback, useEffect, useRef, useState } from "react";
import { IconMaximize, IconMinus, IconPlus, IconRadar2 } from "@tabler/icons-react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import type { NodePosition } from "../../engine/networkLayout";
import { nodeTypeText } from "../../i18n/enumText";
import type { NetworkNode } from "../../types";
import { cx } from "../common/cx";
import { NodeIcon, RISK_CLASS, type NodeVerdict } from "./nodeView";
import { riskTintClass } from "./riskTint";
import styles from "./NetworkCanvas.module.css";

const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;
const DRAG_THRESHOLD_PX = 4;

interface Transform {
  scale: number;
  tx: number;
  ty: number;
}

interface Edge {
  key: string;
  a: NodePosition;
  b: NodePosition;
  hot: boolean;
}

export interface NetworkCanvasProps {
  nodes: readonly NetworkNode[];
  positions: readonly NodePosition[];
  selectedNodeId: string | null;
  linkedIds: ReadonlySet<string>;
  marked: ReadonlySet<string>;
  scannedIds: ReadonlySet<string>;
  submitted: boolean;
  verdictOf: (node: NetworkNode) => NodeVerdict;
  firstRiskyId: string | null;
  onSelect: (id: string) => void;
}

function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

function touchDistance(
  a: { clientX: number; clientY: number },
  b: { clientX: number; clientY: number },
): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

export function NetworkCanvas({
  nodes,
  positions,
  selectedNodeId,
  linkedIds,
  marked,
  scannedIds,
  submitted,
  verdictOf,
  firstRiskyId,
  onSelect,
}: NetworkCanvasProps): JSX.Element {
  const { i18n } = useLingui();
  const viewportRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState<Transform>({ scale: 1, tx: 0, ty: 0 });
  const drag = useRef<{
    x: number;
    y: number;
    tx: number;
    ty: number;
    pointerId: number;
    active: boolean;
  } | null>(null);
  const pinch = useRef<number | null>(null);

  const posOf = new Map(positions.map((p) => [p.id, p]));

  const edges: Edge[] = [];
  const seen = new Set<string>();
  for (const n of nodes) {
    for (const c of n.connections) {
      const a = posOf.get(n.id);
      const b = posOf.get(c);
      if (!a || !b) {
        continue;
      }
      const key = n.id < c ? `${n.id}|${c}` : `${c}|${n.id}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      edges.push({ key, a, b, hot: selectedNodeId === n.id || selectedNodeId === c });
    }
  }

  const zoomAt = useCallback((factor: number, px: number, py: number) => {
    setTransform((prev) => {
      const scale = clampScale(prev.scale * factor);
      const k = scale / prev.scale;
      return { scale, tx: px - (px - prev.tx) * k, ty: py - (py - prev.ty) * k };
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomAt(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX - rect.left, e.clientY - rect.top);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.pointerType === "mouse" && e.button !== 0) {
      return;
    }
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      tx: transform.tx,
      ty: transform.ty,
      pointerId: e.pointerId,
      active: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const start = drag.current;
    if (!start) {
      return;
    }
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (!start.active) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      start.active = true;
      e.currentTarget.setPointerCapture?.(start.pointerId);
    }
    setTransform((prev) => ({ ...prev, tx: start.tx + dx, ty: start.ty + dy }));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const start = drag.current;
    if (start?.active) {
      e.currentTarget.releasePointerCapture?.(start.pointerId);
    }
    drag.current = null;
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>): void => {
    if (e.touches.length !== 2) {
      return;
    }
    const a = e.touches[0];
    const b = e.touches[1];
    if (!a || !b) {
      return;
    }
    const dist = touchDistance(a, b);
    if (pinch.current !== null) {
      const rect = viewportRef.current?.getBoundingClientRect();
      const mx = (a.clientX + b.clientX) / 2 - (rect?.left ?? 0);
      const my = (a.clientY + b.clientY) / 2 - (rect?.top ?? 0);
      zoomAt(dist / pinch.current, mx, my);
    }
    pinch.current = dist;
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>): void => {
    if (e.touches.length < 2) {
      pinch.current = null;
    }
  };

  const center = (): { px: number; py: number } => {
    const rect = viewportRef.current?.getBoundingClientRect();
    return { px: (rect?.width ?? 0) / 2, py: (rect?.height ?? 0) / 2 };
  };

  return (
    <div className={styles.canvas}>
      <div
        ref={viewportRef}
        className={styles.viewport}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className={styles.world}
          style={{ transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})` }}
        >
          <svg className={styles.edges} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {edges.map((edge) => (
              <line
                key={edge.key}
                x1={edge.a.x}
                y1={edge.a.y}
                x2={edge.b.x}
                y2={edge.b.y}
                stroke={edge.hot ? "var(--accent)" : "var(--border-strong)"}
                strokeWidth={edge.hot ? 2 : 1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
          {nodes.map((node) => {
            const p = posOf.get(node.id);
            if (!p) {
              return null;
            }
            const isSelected = node.id === selectedNodeId;
            const isLinked = linkedIds.has(node.id) && !isSelected;
            const isMarked = marked.has(node.id);
            const scanned = scannedIds.has(node.id);
            const verdict = submitted ? verdictOf(node) : null;
            return (
              <button
                type="button"
                key={node.id}
                data-tutorial={node.id === firstRiskyId ? "risky-node" : undefined}
                className={cx(
                  styles.node,
                  riskTintClass(RISK_CLASS, node.riskLevel, submitted),
                  isSelected && styles.selected,
                  isLinked && styles.linked,
                  scanned && styles.scanned,
                  verdict && styles[verdict],
                )}
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
                aria-pressed={isSelected}
                onClick={() => onSelect(node.id)}
              >
                <span className={styles.nodeIco}>
                  <NodeIcon type={node.type} />
                </span>
                <span>{i18n._(nodeTypeText(node.type))}</span>
                {isMarked && <span className={styles.flagDot} aria-hidden="true" />}
                {scanned && <IconRadar2 size={12} className={styles.scanIco} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </div>
      <div className={styles.controls}>
        <button type="button" aria-label={i18n._(msg`Отдалить`)} onClick={() => { const c = center(); zoomAt(1 / 1.2, c.px, c.py); }}>
          <IconMinus size={16} aria-hidden="true" />
        </button>
        <button type="button" aria-label={i18n._(msg`По размеру`)} onClick={() => setTransform({ scale: 1, tx: 0, ty: 0 })}>
          <IconMaximize size={16} aria-hidden="true" />
        </button>
        <button type="button" aria-label={i18n._(msg`Приблизить`)} onClick={() => { const c = center(); zoomAt(1.2, c.px, c.py); }}>
          <IconPlus size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
